import withRender from './sms_resend_cellphone_recovery.vue.html';

import promisifiedAjaxRequest from '../../helpers/ajaxrequest/promisifiedAjaxRequest';
import promisifiedPrepareErrors from '../../helpers/promisifiedPrepareErrors';

import BMessageError from '../b_message_error/b_message_error';
import Spinner from '../spinner/spinner';

import getErrorTypeMixin from '../../mixins/getErrorType';

import {
    CELLPHONE_RECOVERY_OPERATIONS,
    CELLPHONE_RECOVERY_OPERATIONS_MUTATION_UPDATE_STATE,
} from '../../constants/cellphone_recovery_operations';

export const RESEND_CELLPHONE_RECOVERY_EVENT = 'resend-cellphone-recovery';

export default withRender({
    name: 'sms-verification-resend-cellphone-recovery',
    mixins: [getErrorTypeMixin],
    components: {
        BMessageError,
        Spinner,
    },

    props: {
        operation: {
            type: String,
            required: true,
            validator(value) {
                return Object.values(CELLPHONE_RECOVERY_OPERATIONS).includes(value);
            },
        },
        sentCodesPropName: {
            type: String,
            required: true,
        }
    },

    data() {
        return {
            templatePrefix: 'js-tmpl-sms-verification-resend-cellphone-recovery-error',

            ERRORS_TYPES: {
                requestCode: ['__all__:can_not_request', '__all__:sms_error'],
            },

            error: false,
            errors: [],
            processing: false,

            showTip: false,
            firstRequestSent: false,
            isTimerActive: false,
            now: Math.floor(Date.now() / 1000),
            updateNowInterval: null,
            timerResendAvailableAt: 0,
            timerWindowAvailableAt: 0,
            timerIsLastAttempt: false,
            timerKey: 0,
        };
    },
    watch: {
        /* eslint-disable max-len */
        resendAvailableAt: {
            handler(newVal, oldVal) {
                // If newVal is 0 or undefined, ignore it (don't let the timer start)
                if (!newVal) return;

                const currentNow = Math.floor(Date.now() / 1000);

                // Updating the local state
                this.timerResendAvailableAt = newVal;

                // Restart the timer (change the key) ONLY if:
                // 1. Time has really changed (newVal !== oldVal)
                // 2. And this time is in the future
                if (newVal !== oldVal && newVal > currentNow) {
                    this.timerKey += 1;
                }
            },
            // Remove immediate: true so as not to pull the timer when mounting for the first time,
            // if the data has not arrived yet or is being uploaded
            immediate: false
        },
        windowAvailableAt: {
            handler(newVal) {
                this.timerWindowAvailableAt = newVal;
            },
            immediate: true
        },
        isLastAttempt: {
            handler(newVal) {
                this.timerIsLastAttempt = newVal;
            },
            immediate: true
        }
    },

    computed: {
        operationStore() {
            return this.$store.state[this.operation];
        },
        codeExpiresAt() {
            return this.operationStore.codeExpiresAt;
        },
        codeLimit() {
            return this.operationStore.codeLimit;
        },
        codeLimitTimeout() {
            return this.operationStore.codeLimitTimeout;
        },
        codeRequestTimeout() {
            return this.operationStore.codeRequestTimeout;
        },
        requestNewCodeUrl() {
            return this.operationStore.urls.cellphoneRecoveryResendSmsUrl;
        },
        countrySelectOptions() {
            return this.operationStore.CountrySelectOptions;
        },
        sentCodes() {
            return this.operationStore[this.sentCodesPropName];
        },
        requestButtonDisabled() {
            if (this.resendAvailableAt) {
                return this.now <= this.resendAvailableAt;
            }

            return false;
        },
        isLastAttempt() {
            /* eslint-disable max-len */
            if (this.localIsLastAttemptOverride !== null) {
                return this.localIsLastAttemptOverride;
            }

            const recoveryState = this.$store.state.cellphone_recovery || {};
            const opStore = this.operationStore;
            let flag = opStore.is_last_attempt;

            if (flag === undefined) { flag = opStore.isLastAttempt; }
            if (flag === undefined) { flag = recoveryState.is_last_attempt || recoveryState.isLastAttempt; }

            return flag === true || flag === 'true';
        },
        resendAvailableAt() {
            if (this.localResetTimestamp !== null) {
                return this.localResetTimestamp;
            }

            const recoveryState = this.$store.state.cellphone_recovery || {};
            const opStore = this.operationStore;

            return opStore.resend_available_at
                || opStore.resendAvailableAt
                || recoveryState.resend_available_at
                || recoveryState.resendAvailableAt
                || this.codeExpiresAt
                || 0;
        },
        windowAvailableAt() {
            if (this.localWindowAvailableAtOverride !== null) {
                return this.localWindowAvailableAtOverride;
            }

            const recoveryState = this.$store.state.cellphone_recovery || {};
            const opStore = this.operationStore;

            return opStore.window_available_at
                || opStore.windowAvailableAt
                || recoveryState.window_available_at
                || recoveryState.windowAvailableAt
                || 0;
        },
        countryCodeLetters() {
            return this.getCountryCodeLetters();
        },
        shortCellphone() {
            return this.getShortCellphone();
        },
    },

    methods: {
        findCountryOptionByNumber(fullNumber) {
            if (!fullNumber) return null;
            return this.countrySelectOptions.find(option => {
                const codeStr = String(option.cellphone_code);
                return fullNumber.startsWith(codeStr);
            });
        },
        // Получение кода страны
        getCountryCodeLetters() {
            const fullNumber = this.operationStore.cellphone;
            const countryOption = this.findCountryOptionByNumber(fullNumber);
            return countryOption ? countryOption.value : '';
        },
        // Получение короткого номера
        getShortCellphone() {
            const fullNumber = this.operationStore.cellphone;
            const countryOption = this.findCountryOptionByNumber(fullNumber);
            if (countryOption) {
                const codeLength = String(countryOption.cellphone_code).length;
                return fullNumber.slice(codeLength);
            }
            return fullNumber;
        },
        toggleTip() {
            this.showTip = !this.showTip;
        },
        updateNow() {
            this.now = Math.floor(Date.now() / 1000);
        },
        async requestNewCode() {
            /* eslint-disable max-len */
            if (this.requestButtonDisabled || this.processing || this.isTimerActive) {
                return;
            }

            this.processing = true;
            this.error = false;
            const data = new FormData();
            data.append('country_code', this.countryCodeLetters);
            data.append('cellphone', this.shortCellphone);

            try {
                const result = await promisifiedAjaxRequest({
                    url: this.requestNewCodeUrl,
                    method: 'POST',
                    data
                });

                // --- НОВЫЙ КОД: Проверяем метод и эмитим событие ---
                if (result.twofactor_method) {
                    // Отправляем событие наверх, чтобы родительская форма могла перерисоваться
                    this.$emit('method-changed', result.twofactor_method);
                }
                // ---------------------------------------------------

                const hasServerLimits = result && ('is_last_attempt' in result || 'resend_available_at' in result);

                let codeExpiresAt;
                let serverIsLastAttempt = false;
                let serverWindowAvailableAt = 0;

                if (hasServerLimits) {
                    serverIsLastAttempt = result.is_last_attempt === true || result.is_last_attempt === 'true';
                    serverWindowAvailableAt = parseInt(result.window_available_at, 10) || 0;
                    codeExpiresAt = parseInt(result.resend_available_at, 10) || 0;

                    const currentNow = Math.floor(Date.now() / 1000);

                    // If the backend returned 200 OK, but the time is already in the past
                    // the SMS was not sent, the cooldown did not arrive from the server,
                    // the frontend takes the initiative and forces a standard timeout.
                    if (codeExpiresAt <= currentNow) {
                        const opStore = this.operationStore || {};
                        const timeout = parseInt(this.codeRequestTimeout || opStore.code_request_timeout || opStore.codeRequestTimeout, 10) || 60;
                        codeExpiresAt = currentNow + timeout;
                    }
                } else {
                    const opStore = this.operationStore || {};
                    const timeout = parseInt(this.codeRequestTimeout || opStore.code_request_timeout || opStore.codeRequestTimeout, 10) || 60;
                    const currentNow = Math.floor(Date.now() / 1000);

                    codeExpiresAt = currentNow + timeout;

                    const currentSentCodes = parseInt(this.sentCodes || opStore[this.sentCodesPropName], 10) || 0;
                    result[this.sentCodesPropName] = currentSentCodes + 1;
                }

                if (this.$store && this.$store.state && this.$store.state.cellphone_recovery) {
                    const globalState = this.$store.state.cellphone_recovery;
                    globalState.is_last_attempt = serverIsLastAttempt;
                    globalState.isLastAttempt = serverIsLastAttempt;
                    globalState.resend_available_at = codeExpiresAt;
                    globalState.resendAvailableAt = codeExpiresAt;
                    globalState.window_available_at = serverWindowAvailableAt;
                    globalState.windowAvailableAt = serverWindowAvailableAt;
                    if (result.twofactor_method) {
                        globalState.twofactorMethod = result.twofactor_method;

                        if (result.twofactor_method === 'sms') {
                            globalState.notificationType = 'sms';
                        }
                    }
                }

                this.timerResendAvailableAt = codeExpiresAt;
                this.timerWindowAvailableAt = serverWindowAvailableAt;
                this.timerIsLastAttempt = serverIsLastAttempt;

                const updateStateMutation = CELLPHONE_RECOVERY_OPERATIONS_MUTATION_UPDATE_STATE[this.operation];
                const mutationPayload = {
                    codeExpiresAt,
                    is_last_attempt: serverIsLastAttempt,
                    isLastAttempt: serverIsLastAttempt,
                    resend_available_at: codeExpiresAt,
                    resendAvailableAt: codeExpiresAt,
                    window_available_at: serverWindowAvailableAt,
                    windowAvailableAt: serverWindowAvailableAt,
                    twofactor_method: result.twofactor_method,
                };
                if (this.sentCodesPropName in result) {
                    mutationPayload[this.sentCodesPropName] = result[this.sentCodesPropName];
                }
                this.$store.commit(updateStateMutation, mutationPayload);
            } catch (xhrError) {
                const { errors } = await promisifiedPrepareErrors(xhrError);
                this.error = true;
                this.errors = errors;

                if (!this.errorType.requestCode) {
                    this.$emit('error', xhrError);
                }
            }

            if (!this.error || this.errorType.requestCode) {
                this.firstRequestSent = true;
            }

            this.processing = false;
        },
    },

    mounted() {
        this.updateNowInterval = setInterval(this.updateNow, 1000);

        this.$on(RESEND_CELLPHONE_RECOVERY_EVENT, () => {
            this.showTip = true;
            this.requestNewCode();
        });
    },

    beforeDestroy() {
        clearInterval(this.updateNowInterval);
        this.updateNowInterval = null;
        this.$off(RESEND_CELLPHONE_RECOVERY_EVENT);
    },
});
